import * as React from 'react';
import type { IAccount } from '../../../../extViewmodels';
import * as Api from '../../../../extViewmodels';
import { useDebounceValue } from '../../../../hooks/useDebounceValue';
import { useAdminAccountFilterQuery } from '../../../../queries/Admin/Account/useAdminAccountFilterQuery';
import { AccountFilterCriteriaProperty, type IAccountsFilterRequest } from '../../../../viewmodels/AdminViewModels';
import {
	Combobox,
	ComboboxContent,
	ComboboxInput,
	ComboboxList,
	ComboboxStatus,
} from '../../../../web/components-v2/Combobox';
import {
	ComboboxAvatarItem,
	ComboboxAvatarItemAvatar,
	ComboboxAvatarItemTitle,
} from '../../../../web/components-v2/Combobox/ComboboxAvatarItem';
import type { NewTicketDraftAccount } from './newTicketDraft';

interface AccountFieldProps {
	account: NewTicketDraftAccount | null;
	onChange: (account: NewTicketDraftAccount | null) => void;
}

/** Account combobox backed by the same admin account search the inbox's scope picker uses. */
export const AccountField: React.FC<AccountFieldProps> = ({ account, onChange }) => {
	const [searchValue, setSearchValue] = React.useState(account?.name ?? '');
	const debouncedSearchValue = useDebounceValue(searchValue.trim(), 250);
	const accountFilter = React.useMemo<IAccountsFilterRequest>(() => {
		const criteria: Api.IFilterCriteria<AccountFilterCriteriaProperty>[] = [];
		if (debouncedSearchValue) {
			criteria.push({ property: AccountFilterCriteriaProperty.Name, value: debouncedSearchValue });
		}
		return { criteria, op: Api.FilterOperator.And };
	}, [debouncedSearchValue]);
	const accountsQuery = useAdminAccountFilterQuery({
		filter: accountFilter,
		autocompleteFragment: debouncedSearchValue || undefined,
		pageSize: 20,
		enabled: debouncedSearchValue.length > 0,
	});
	const accounts = React.useMemo(
		() => accountsQuery.data?.pages.flatMap(page => page.values ?? []) ?? [],
		[accountsQuery.data]
	);
	const selectedAccount = accounts.find(item => item.id === account?.id) ?? null;

	// Keep the input text in sync when the selection changes from outside the combobox
	// (a stored draft hydrating, or a picked contact carrying its own account).
	const accountId = account?.id;
	const accountName = account?.name;
	React.useEffect(() => {
		setSearchValue(accountName ?? '');
	}, [accountId, accountName]);

	return (
		<Combobox
			items={accounts}
			value={selectedAccount}
			onValueChange={(next: IAccount | null) => {
				if (next?.id) {
					onChange({ id: next.id, name: next.companyName });
					setSearchValue(next.companyName ?? '');
				} else {
					onChange(null);
					setSearchValue('');
				}
			}}
			inputValue={searchValue}
			onInputValueChange={setSearchValue}
			itemToStringLabel={(item: IAccount) => item.companyName || ''}
			isItemEqualToValue={(item: IAccount, value: IAccount) => item.id === value.id}
		>
			<ComboboxInput aria-label='Search accounts' placeholder='Search accounts...' showClear={!!account} />
			<ComboboxContent>
				<ComboboxStatus>
					{accountsQuery.isFetching
						? 'Searching...'
						: accounts.length === 0 && debouncedSearchValue
							? `No accounts found for "${debouncedSearchValue}"`
							: 'Search accounts by name'}
				</ComboboxStatus>
				<ComboboxList>
					{(item: IAccount) => (
						<ComboboxAvatarItem key={item.id} value={item}>
							<ComboboxAvatarItemAvatar
								imageUrl=''
								fallbackText={(item.companyName || 'AC').substring(0, 2).toUpperCase()}
							/>
							<ComboboxAvatarItemTitle>{item.companyName || item.id}</ComboboxAvatarItemTitle>
						</ComboboxAvatarItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
};
